// GLOBAL SMART SEARCH — the Cmd+K palette. One input over everything the
// caller can reach: server-ranked entities from GET /api/search (elements,
// sources, targets, rules, reference lists, apps, agents, models, reports,
// board tasks, projects) plus a client-side "Pages" group over the nav
// surface. Results are grouped, keyboard-navigable, and deep-link into the
// owning page (project hits switch projects).
//
// Mounted once in Layout (full chrome only — portal and share-only navs are
// deliberately trimmed surfaces and keep their curated lists). Opens on
// Cmd/Ctrl+K, the sidebar Search button, or a `cortex:open-search` event.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppWindow,
  Blocks,
  Bot,
  Box,
  Brain,
  ClipboardList,
  Compass,
  Database,
  FileText,
  FolderOpen,
  List,
  Loader2,
  Puzzle,
  Search,
} from "lucide-react";
import { createPageUrl } from "@/utils";
import { projectPath } from "@/utils/projectPath";
import { searchGlobal } from "@/services/search";
import { useAuth } from "../../contexts/AuthContext";
import { isMindshineStaff } from "../../utils/staff";

// Client-side twin of the backend's pure ranking (backend/modules/search —
// the canonical version, unit-tested there). Only the Pages group is scored
// here; keep the tiers in step if the backend's change.
function tokenize(q) {
  return String(q || "").toLowerCase().split(/\s+/).map((t) => t.trim()).filter(Boolean).slice(0, 8);
}
function scoreItem(tokens, rawName, rawSecondary) {
  const name = String(rawName || "").toLowerCase();
  if (!name) return 0;
  const words = name.split(/[^a-z0-9]+/).filter(Boolean);
  const secondary = String(rawSecondary || "").toLowerCase();
  let total = 0;
  for (const token of tokens) {
    let s = 0;
    if (name === token) s = 100;
    else if (name.startsWith(token)) s = 80;
    else if (words.some((w) => w.startsWith(token))) s = 70;
    else if (name.includes(token)) s = 55;
    else if (secondary.includes(token)) s = 30;
    if (s === 0) return 0;
    total += s;
  }
  return total + Math.max(0, 20 - name.length) / 20;
}

// The navigable page surface (mirrors the sidebar's nav items; gated entries
// join per-caller below, exactly like SidebarInner's booleans).
const CORE_PAGES = [
  { title: "Dashboard", page: "dashboard" },
  { title: "Alerts", page: "alerts" },
  { title: "Board", page: "board" },
  { title: "Apps", page: "apps" },
  { title: "Agents", page: "agents" },
  { title: "Docs", page: "docs" },
  { title: "Guides", page: "guides" },
  { title: "Models", page: "models" },
  { title: "Reports", page: "reports" },
  { title: "Workflows", page: "workflows" },
  { title: "Data Dictionary", page: "data-dictionary" },
  { title: "Data Elements", page: "data-elements" },
  { title: "Data Sources", page: "data-sources" },
  { title: "Source Mappings", page: "source-mappings" },
  { title: "Reference Lists", page: "reference-lists" },
  { title: "Processing Rules", page: "processing-rules" },
  { title: "Data Review", page: "data-review" },
  { title: "Quality", page: "dictionary-quality" },
  { title: "Pipeline", page: "pipeline" },
  { title: "Deployments", page: "deployments" },
  { title: "Branches", page: "branches" },
  { title: "Audit", page: "audit" },
  { title: "Projects", page: "projects" },
  { title: "Settings", page: "settings" },
  { title: "Profile", page: "profile" },
];

const GROUP_ICONS = {
  element: Blocks,
  source: Box,
  target: Database,
  rule: Puzzle,
  reference_list: List,
  app: AppWindow,
  agent: Bot,
  model: Brain,
  report: FileText,
  task: ClipboardList,
  project: FolderOpen,
  page: Compass,
};

const MIN_CHARS = 2;
const DEBOUNCE_MS = 200;

export default function SearchPalette() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const seqRef = useRef(0);
  const debounceRef = useRef(null);

  const pages = useMemo(() => {
    const staff = isMindshineStaff(user);
    const showOrgs = !!user?.orgs?.some((o) => o.role === "platform_admin" || o.role === "org_admin");
    const platformAdmin = !!user?.orgs?.some((o) => o.role === "platform_admin");
    return [
      ...CORE_PAGES,
      ...(showOrgs ? [{ title: "Organizations", page: "organizations" }] : []),
      ...(platformAdmin ? [{ title: "Templates", page: "templates" }] : []),
      ...(staff
        ? [
            { title: "Flow", page: "flow" },
            { title: "Agent fleet", page: "fleet" },
            { title: "Cortex Audit", page: "cortex-audit" },
          ]
        : []),
    ];
  }, [user]);

  // Open/close triggers: Cmd/Ctrl+K anywhere, plus the sidebar button's event.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("cortex:open-search", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("cortex:open-search", onOpen);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setQ("");
      setGroups([]);
      setActiveIndex(0);
      // Focus after the overlay paints.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Debounced server search; stale responses are dropped by sequence number.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const query = q.trim();
    if (query.length < MIN_CHARS) {
      setGroups([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const seq = ++seqRef.current;
      try {
        const res = await searchGlobal(query);
        if (seq === seqRef.current) {
          setGroups(res);
          setActiveIndex(0);
        }
      } catch {
        if (seq === seqRef.current) setGroups([]);
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [q, open]);

  // Pages group is client-side (static list — no server hop needed).
  const pageGroup = useMemo(() => {
    const tokens = tokenize(q);
    if (q.trim().length < MIN_CHARS || tokens.length === 0) return null;
    const items = pages
      .map((p) => ({ ...p, score: scoreItem(tokens, p.title) }))
      .filter((p) => p.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map((p) => ({ id: p.page, name: p.title, description: null, url: p.page, score: p.score }));
    return items.length ? { kind: "page", label: "Pages", items } : null;
  }, [q, pages]);

  // Merge server groups + pages, ordered by best hit; flatten for keyboard nav.
  const allGroups = useMemo(() => {
    const merged = pageGroup ? [...groups, pageGroup] : groups;
    return [...merged].sort((a, b) => (b.items[0]?.score || 0) - (a.items[0]?.score || 0));
  }, [groups, pageGroup]);
  const flat = useMemo(
    () => allGroups.flatMap((g) => g.items.map((item) => ({ kind: g.kind, item }))),
    [allGroups]
  );

  const select = useCallback(
    (kind, item) => {
      setOpen(false);
      if (kind === "project") navigate(item.url); // absolute — switches project
      else if (kind === "page") navigate(createPageUrl(item.url));
      else navigate(projectPath(item.url));
    },
    [navigate]
  );

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (flat.length ? (i + 1) % flat.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (flat.length ? (i - 1 + flat.length) % flat.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const hit = flat[activeIndex];
      if (hit) select(hit.kind, hit.item);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Keep the active row visible while arrowing through a long list.
  const listRef = useRef(null);
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, flat.length]);

  if (!open) return null;

  let flatIndex = -1;
  return (
    <div
      className="fixed inset-0 z-[100000] bg-black/40 flex justify-center items-start pt-[12vh] px-4"
      data-testid="search-palette"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-xl flex flex-col overflow-hidden border border-black/10 dark:border-white/10">
        <div className="flex items-center gap-2 px-4 h-12 border-b border-black/10 dark:border-white/10 shrink-0">
          {loading ? (
            <Loader2 size={17} className="shrink-0 text-slate-400 animate-spin" />
          ) : (
            <Search size={17} className="shrink-0 text-slate-400" />
          )}
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Search everything — elements, sources, rules, apps, tasks, projects…"
            data-testid="search-palette-input"
            className="flex-1 h-full bg-transparent border-0 outline-none text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400"
          />
          <kbd className="hidden sm:inline-flex items-center rounded border border-black/10 dark:border-white/15 px-1.5 py-0.5 text-[10px] font-medium text-slate-400">
            esc
          </kbd>
        </div>
        <div ref={listRef} className="max-h-[52vh] overflow-y-auto overscroll-contain py-1" data-testid="search-palette-results">
          {q.trim().length < MIN_CHARS ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400">
              Type at least {MIN_CHARS} characters to search this project, its apps and your projects.
            </div>
          ) : flat.length === 0 && !loading ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400" data-testid="search-palette-empty">
              Nothing matches “{q.trim()}”.
            </div>
          ) : (
            allGroups.map((g) => {
              const Icon = GROUP_ICONS[g.kind] || Search;
              return (
                <div key={g.kind}>
                  <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    {g.label}
                  </div>
                  {g.items.map((item) => {
                    flatIndex += 1;
                    const idx = flatIndex;
                    const active = idx === activeIndex;
                    return (
                      <button
                        key={`${g.kind}:${item.id}`}
                        type="button"
                        data-active={active || undefined}
                        data-testid="search-palette-result"
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => select(g.kind, item)}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-left border-0 cursor-pointer transition-colors ${
                          active
                            ? "bg-indigo-50 dark:bg-indigo-950/60"
                            : "bg-transparent"
                        }`}
                      >
                        <span className="shrink-0 text-base leading-none w-5 text-center">
                          {item.meta?.icon ? (
                            item.meta.icon
                          ) : (
                            <Icon size={16} className="text-slate-400 inline-block" />
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                            {item.name}
                          </span>
                          {item.description ? (
                            <span className="block text-xs text-slate-500 dark:text-slate-400 truncate">
                              {item.description}
                            </span>
                          ) : null}
                        </span>
                        {item.meta?.status ? (
                          <span className="shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
                            {item.meta.status}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
        <div className="flex items-center gap-3 px-4 h-8 border-t border-black/10 dark:border-white/10 text-[11px] text-slate-400 shrink-0">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span className="ml-auto">smart search across the whole workspace</span>
        </div>
      </div>
    </div>
  );
}
